import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { h4, paragraphMedium } from '@helpers/typography';
import { verticalFadeIn } from '@helpers/variants';
import HexagonSolidSvg from '@img/shapes/hexagon-solid.svg';
import BottomRightSvg from '@img/shapes/shape-group.svg';
import Button from '@stories/Components/Buttons/GeneralButton/Button';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled, { createGlobalStyle, css } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --headerHeight: ${fluidAdjusted(65, 99, Device.Mobile, Device.DesktopSmall)} !important;

    @media ${from(Device.DesktopSmall)} {
      --headerHeight: 192px !important;
    }
  }

  .raise-index {
    position: relative;
    z-index: 11;
  }
`;
const Container = styled.header `
  --backgroundColor: transparent;
  --separatorColor: hsla(var(--fgColorString) / 0.2);
  --separatorColorDark: var(--accentColorSecondary);
  --textColor: ${brand.secondary.red5};

  top: 0;
  left: 0;
  width: 100%;
  z-index: 9;

  ${({ isEditMode }) => isEditMode
    ? css `
          position: static;
        `
    : css `
          position: fixed;
        `}
`;
const Overlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${rgba(brand.black, 0.9)};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  z-index: -1;

  @media ${until(Device.DesktopSmall)} {
    background-color: ${brand.buff.buff1};
  }

  &[data-action-open='true'] {
    opacity: 1;
    pointer-events: all;
  }
`;
const ShapeBottomRight = styled(BottomRightSvg) `
  --color: ${rgba(brand.buff.buff4, 0.42)};
  color: var(--color);

  position: absolute;
  bottom: 0;
  right: -20px;
  height: 70%;
  z-index: 1;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const Inner = styled.div `
  ${siteWide};
  position: relative;
`;
const MainContainer = styled.div `
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  padding: ${fluidAdjusted(20, 36)} 0 0;
  position: relative;
  z-index: 1;

  > ${Inner} {
    ${baseGrid};

    align-items: center;

    @media ${until(Device.DesktopSmall)} {
      gap: 12px;
    }
  }
`;
const LogoWrapper = styled.a `
  display: block;
  grid-column: 1 / span 2;

  @media ${until(Device.Tablet)} {
    grid-column: 1 / span 3;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }
`;
const Logo = styled.img `
  display: block;
  max-height: 48px;

  @media ${from(Device.Tablet)} {
    max-height: ${fluidAdjusted(48, 120)};
  }
`;
const ActionWrapper = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            y: '-50%',
        },
        visible: {
            opacity: 1,
            y: '0%',
        },
    },
    transition: {
        duration: 0.45,
    },
})) `
  grid-column: 7 / span 6;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${fluidAdjusted(18, 36)};
  background-color: transparent;
  padding: 0 2px;

  @media ${from(Device.DesktopSmall)} {
    grid-column: 3 / span 10;
    position: relative;
    box-shadow: none;
    padding: 14px 2px;
  }

  [data-show-bg='true'] &,
  &:hover {
    @media ${from(Device.DesktopSmall)} {
      background-color: ${brand.buff.buff1};
      filter: drop-shadow(0px 6px 4px rgba(0, 0, 0, 0.16));
    }
  }

  @media ${from(Device.Desktop)} {
    grid-column: 4 / span 9;
  }

  @media ${from(Device.DesktopLarge)} {
    grid-column: 5 / span 8;
  }

  > ${ButtonStyles.Button} {
    order: 3;
    white-space: nowrap;

    @media ${until(Device.DesktopSmall)} {
      display: none;
    }
  }
`;
const NavStart = styled.div `
  position: absolute;
  top: 0;
  right: calc(100% - 1px);
  height: 100%;
  display: none;

  @media ${from(Device.DesktopSmall)} {
    display: block;
    opacity: 0;
  }

  [data-show-bg='true'] &,
  ${ActionWrapper}:hover & {
    @media ${from(Device.DesktopSmall)} {
      opacity: 1;
    }
  }

  svg {
    height: 100%;
    color: ${brand.buff.buff1};
  }
`;
const NavEnd = styled.div `
  position: absolute;
  top: 0;
  left: calc(100% - 1px);
  height: 100%;
  display: none;

  @media ${from(Device.DesktopSmall)} {
    display: block;
    opacity: 0;
  }

  [data-show-bg='true'] &,
  ${ActionWrapper}:hover & {
    @media ${from(Device.DesktopSmall)} {
      opacity: 1;
    }
  }

  svg {
    height: 100%;
    color: ${brand.buff.buff1};
    transform: scaleX(-1);
  }
`;
const SearchButton = styled(Button) `
  order: 2;

  ${ActionWrapper}[data-action-open='true'] & {
    display: none;
  }
`;
const ButtonBg = styled(HexagonSolidSvg) `
  color: ${brand.buff.buff4};
`;
const MobileSearch = styled.button `
  ${ButtonReset};

  color: var(--textColor);

  width: 53px;
  height: 48px;
  position: relative;

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    height: 20px;
    width: 20px;
  }

  ${ButtonBg} {
    width: 53px;
    height: 48px;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};

  color: var(--textColor);

  width: 53px;
  height: 48px;
  position: relative;

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    height: 20px;
    width: 20px;
  }

  ${ButtonBg} {
    width: 53px;
    height: 48px;
  }

  ${ActionWrapper}[data-action-open='false'] & {
    display: none;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MenuButton = styled.button `
  ${ButtonReset};
  color: var(--textColor);

  width: 53px;
  height: 48px;
  position: relative;

  order: 3;

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    height: 20px;
    width: 20px;
  }

  ${ButtonBg} {
    width: 53px;
    height: 48px;
  }

  ${ActionWrapper}[data-action-open='true'] & {
    display: none;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MobileNav = styled.nav `
  ${transition('transform')};

  position: fixed;
  height: calc(100dvh - var(--headerHeight));
  width: 100%;
  top: var(--headerHeight);
  background-color: var(--backgroundColor);
  overflow: auto;
  color: var(--textColor);

  &[data-is-open='false'] {
    transform: translateX(100%);
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MobileNavInner = styled.div `
  width: 100%;
`;
const PrimaryNavLinkStyles = css `
  font-weight: 700;
  display: block;
  font-size: 16px;
  line-height: 20px;
  color: currentColor;
  text-decoration: none;

  &:focus {
    outline: none;
  }
`;
const NavLinkStyles = css `
  ${transition('text-decoration-color')};

  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${brand.secondary.red5};

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: currentColor;
  }
`;
const MobileNavButton = styled.button `
  ${ButtonReset};
  ${PrimaryNavLinkStyles};

  padding: 14px var(--sitePadding);
  border-bottom: 1px solid var(--separatorColor);
  position: relative;
  width: 100%;
  text-align: left;

  &:not(a) {
    &::before,
    &::after {
      display: block;
      content: '';
      position: absolute;
      right: var(--sitePadding);
      top: 50%;
      width: 14px;
      height: 2px;
      background-color: var(--textColor);
    }

    &[data-is-open='true']::after,
    &::before {
      transform: translateY(-50%);
    }

    &::after {
      ${transition('transform')};

      transform: translateY(-50%) rotate(90deg);
    }
  }

  &[data-indent='true'] {
    padding-left: calc(var(--sitePadding) * 2);
    background-color: var(--highlightColorPrimary);

    a& {
      font-weight: 400;
    }
  }
`;
const MobileNavLink = styled.a `
  ${NavLinkStyles};

  display: block;
  padding: 14px var(--sitePadding) 14px calc(var(--sitePadding) * 3);
  border-bottom: 1px solid var(--separatorColor);
  width: 100%;
`;
const MobileColumnsWrapper = styled(m.div) `
  overflow: hidden;
`;
const DesktopNav = styled.nav `
  background: var(--backgroundColor);
  color: var(--textColor);
  border-bottom: 1px solid var(--separatorColor);
  flex: 1 0 auto;

  > ${Inner} {
    --sitePadding: 16px;
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const DesktopNavItem = styled.li ``;
const DesktopPrimaryNavLink = styled.a `
  ${transition('border-color')};
  ${PrimaryNavLinkStyles};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${brand.secondary.red5};

  padding: 17px 0;
  border-bottom: 5px solid transparent;

  [data-dark-bg='true'] & {
    color: ${brand.white};
  }

  &:hover {
    cursor: pointer;
  }

  [data-show-bg='true'] &,
  ${ActionWrapper}:hover & {
    color: ${brand.secondary.red5};
  }

  &.focus-ring,
  ${DesktopNavItem}[data-is-active='true'] & {
    border-color: ${brand.secondary.red5};
    color: ${brand.secondary.red5};
  }
`;
const PlusIcon = styled.div `
  position: relative;
  width: 12px;
  height: 12px;
`;
const PlusHorizontal = styled.div `
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 12px;
  height: 2px;
  border-radius: 1px;
  background-color: currentColor;
`;
const PlusVertical = styled.div `
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 12px;
  background-color: currentColor;
  transition: transform 0.3s;
  border-radius: 1px;

  ${DesktopNavItem}:hover &,
  ${DesktopNavItem}[data-is-active='true'] &,
  .focus-ring & {
    transform: translateX(-50%) rotate(90deg);
  }
`;
const DropdownMenuWrapper = styled.div `
  ${scrollbars};
  ${transition('opacity')};

  font-family: var(--fontFamily);
  position: absolute;
  top: calc(100% + 14px);
  left: 0;
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  width: 100%;
  background-color: ${brand.white};
  border-bottom: 1px solid var(--separatorColorDark);
  border-radius: 0 0 16px 16px;
  box-shadow: inset 0px 18px 11px -19px rgba(0, 0, 0, 0.36);

  ${DesktopNavItem}[data-is-active='true'] & {
    max-height: calc(100vh - var(--headerHeight));
    opacity: 1;
    overflow: auto;
  }

  > ${Inner} {
    display: flex;
    padding: 0;
  }

  &[data-has-featured-item='false'] > ${Inner} {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const IntroColumn = styled.div `
  flex: 0 0 33.3333%;
  background-color: ${brand.buff.buff1};
  padding: 20px 30px;
  box-shadow: inset 0px 18px 11px -19px rgba(0, 0, 0, 0.36);
`;
const IntroContent = styled.p `
  ${paragraphMedium};

  font-size: ${fluidAdjusted(14, 20)};
  line-height: ${fluidAdjusted(20, 24)};
`;
const IntroTitle = styled.h3 `
  ${h4};
  color: ${brand.secondary.red5};
`;
const DropdownMenuFeaturedLink = styled.a `
  ${PrimaryNavLinkStyles};

  display: inline-flex;
  gap: 10px;
  align-items: center;

  > svg {
    ${transition('transform')};

    width: 22px;
    height: 14px;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &:hover {
    cursor: pointer;
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: currentColor;

    > svg {
      transform: translateX(15%);
    }
  }
`;
const DropdownMenuItem = styled.li `
  ${transition('padding, background-color')};

  padding: 10px 15px;
  border-radius: 8px;
  position: relative;
  max-width: 50%;

  &:hover {
    background-color: ${brand.buff.buff4};
  }

  > ${DropdownMenuFeaturedLink} {
    margin-bottom: 12px;
  }
`;
const DropdownMenuColumn = styled.ul `
  padding: 10px 0;
  flex: 1 0 33.3333%;
  background-color: ${brand.buff.buff3};
  box-shadow: inset 0px 18px 11px -19px rgba(0, 0, 0, 0.36);

  &[data-has-child='true'] {
    flex: 0 0 33.3333%;

    ${DropdownMenuItem} {
      max-width: 100%;
    }
  }

  &[data-first-featured-column='true'] {
    margin-left: auto;
  }
`;
const ExtraPadding = styled.div `
  padding: 10px 10px 20px;
`;
const DropdownMenuTitle = styled.li `
  ${PrimaryNavLinkStyles};

  padding-bottom: 8px;
  border-bottom: 2px solid var(--separatorColorDark);
  margin: 10px 0 26px;
`;
const DropdownMenuLink = styled.a `
  ${NavLinkStyles};
  display: flex;
  justify-content: space-between;
  gap: 10px;
  text-decoration: none;
  font-weight: 700;

  svg {
    transform: translateY(4px);
    height: 14px;
    flex: 0 0 8px;
  }

  ${DropdownMenuColumn}[data-has-child='true'] & {
  }
`;
const DropdownMultiLinkSection = styled.div `
  display: none;

  &[data-is-active='true'] {
    flex: 0 1 33.333333%;
    display: flex;
    flex-wrap: wrap;
    align-self: flex-start;
    gap: 0 ${fluidAdjusted(18, 24)};
  }

  ${DropdownMenuColumn}[data-no-group='false'] {
    padding: 10px 0 ${fluidAdjusted(18, 24)};
  }

  ${DropdownMenuItem}[data-group-header='true'] {
    ${DropdownMenuLink} {
      font-weight: 700;
      padding: 5px 0;
    }
  }

  ${DropdownMenuItem} {
    max-width: 100%;
  }

  ${DropdownMenuColumn} {
    flex: 0 1 100%;
    flex-wrap: wrap;
    background-color: transparent;
    box-shadow: none;
  }

  &&& {
    ${DropdownMenuLink} {
      font-weight: 300;
    }
  }
`;
const SearchWrapper = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            y: '50%',
            opacity: 0,
        },
        visible: {
            y: '0%',
            opacity: 1,
        },
    },
    transition: {
        duration: 0.45,
    },
})) `
  grid-column: 3 / -1;
  width: 100%;
  height: 100%;
  position: absolute;

  display: flex;
  align-items: center;

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
  }
`;
const SearchClose = styled.div `
  margin-left: auto;

  @media ${from(Device.TabletLarge)} {
    padding: 14px 2px;
    min-width: 200px;
    text-align: right;
  }
`;
const SearchModalWrapper = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
        },
        visible: {
            opacity: 1,
        },
    },
})) `
  ${({ mainMenuHeight }) => css `
      height: calc(
        100vh - ${mainMenuHeight}px - ${fluidAdjusted(40, 110, Device.Mobile, Device.ActualDesktop)}
      );
    `}
`;
const SearchBar = styled.div `
  @media ${from(Device.TabletLarge)} {
    max-width: 56vw;
  }

  display: flex;
  flex-grow: 1;
  width: 100%;
  padding-bottom: ${fluidAdjusted(10, 20, Device.Mobile, Device.ActualDesktop)};
  border-bottom: 4px solid ${brand.grey.grey35};
  align-items: center;

  opacity: 1;
  transition: opacity 0.15s ease-in-out;

  &[data-disabled='true'] {
    pointer-events: none;
    opacity: 0.5;
  }
`;
const SearchBarInput = styled.input `
  ${h4}
  outline: none;
  flex-grow: 1;
  border: none;
  background-color: transparent;
  margin: 0;
`;
const SearchBarEndContent = styled.div `
  display: flex;
  align-items: center;
  gap: 15px;

  svg {
    color: ${brand.secondary.red5};
    width: ${fluidAdjusted(20, 40, Device.Mobile, Device.ActualDesktop)};
    aspect-ratio: 1 / 1;
    opacity: 0.8;
    cursor: pointer;
  }
`;
const SearchBarClear = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: verticalFadeIn(-25),
})) `
  text-decoration: underline;
  cursor: pointer;
  font-size: 15px;
  font-weight: 900;
`;
const PopularSearches = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: verticalFadeIn(25),
})) `
  display: flex;
  gap: 20px;
  width: 100%;
  position: absolute;
  left: 0;

  @media ${from(Device.TabletLarge)} {
    bottom: -25px;
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
    margin: 25px 0px;
  }

  span {
    font-weight: 900;
    color: ${brand.secondary.red5};
  }
`;
const PopularSearchList = styled.div `
  display: flex;
  gap: 5px 20px;
  flex-wrap: wrap;
`;
const PopularSearch = styled.a `
  color: ${brand.black};
`;
const MobileSearchWrapper = styled.div ``;
const HeaderStyles = {
    ActionWrapper,
    ButtonBg,
    CloseButton,
    Container,
    DesktopNav,
    DesktopNavItem,
    DesktopPrimaryNavLink,
    DropdownMenuColumn,
    DropdownMenuFeaturedLink,
    DropdownMenuItem,
    DropdownMenuLink,
    DropdownMultiLinkSection,
    DropdownMenuTitle,
    DropdownMenuWrapper,
    ExtraPadding,
    GlobalStyles,
    Inner,
    IntroColumn,
    IntroContent,
    IntroTitle,
    Logo,
    LogoWrapper,
    MainContainer,
    MenuButton,
    MobileColumnsWrapper,
    MobileNav,
    MobileNavButton,
    MobileNavInner,
    MobileNavLink,
    MobileSearch,
    NavEnd,
    NavStart,
    Overlay,
    PlusIcon,
    PlusHorizontal,
    PlusVertical,
    SearchButton,
    ShapeBottomRight,
    SearchWrapper,
    SearchClose,
    SearchModalWrapper,
    SearchBar,
    SearchBarInput,
    SearchBarEndContent,
    SearchBarClear,
    PopularSearches,
    PopularSearch,
    MobileSearchWrapper,
    PopularSearchList,
};
export default HeaderStyles;
